import { DEFAULT_LAYOUT_ID } from '@src/components/AppLayout/Layouts/tokens';
import { useBuildDeepDiveSymbolChangePanelParams } from '@src/components/AppLayout/Panels/Trading/DeepDivePanel/useBuildDeepDiveSymbolChangePanelParams';
import { getDeepDiveRoute } from '@src/containers/Routes/routes';
import { DeepDiveProvider, type DeepDiveMarketSelection } from '@src/providers/DeepDiveProvider';
import { decodeOptionalRouteParam } from '@src/utils/decodeRouteParam';
import {
  EMPTY_ARRAY,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  MixpanelSourceContext,
  isMultileg,
  useMixpanel,
  useSecuritiesContext,
  type ResolutionEnumNoPrefix,
} from '@talos/kyoko';
import type { AgAnnotation, AgPriceVolumeChartType } from 'ag-charts-community';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState, type SetStateAction } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  MOBILE_SYMBOL_DEEP_DIVE_MODULES,
  MobileSymbolDeepDiveModuleProvider,
  type MobileSymbolDeepDiveModuleIndex,
} from './MobileSymbolDeepDiveModuleContext';
import { MobileSymbolDeepDiveView } from './MobileSymbolDeepDiveView';

interface MobileSymbolDeepDiveRouteProps {
  navGroupName?: string;
}

function decodeSymbolParam(symbolParam: string | undefined): string | undefined {
  if (symbolParam === '') {
    return undefined;
  }
  return decodeOptionalRouteParam(symbolParam);
}

function getDeepDiveSearchParams(search: string) {
  const searchParams = new URLSearchParams(search);
  return {
    marketAccountNames: searchParams.get('marketAccountNames')?.split(',').filter(Boolean) ?? [],
    marketNames: searchParams.get('marketNames')?.split(',').filter(Boolean) ?? [],
    aggregation: searchParams.get('aggregation') ?? undefined,
  };
}

export function MobileSymbolDeepDiveRoute({ navGroupName }: MobileSymbolDeepDiveRouteProps) {
  const { layoutId, symbol: encodedSymbol } = useParams<{ layoutId: string; symbol: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const mixpanel = useMixpanel();
  const { searchableSecurities, securitiesBySymbol } = useSecuritiesContext();
  const buildSymbolChangePanelParams = useBuildDeepDiveSymbolChangePanelParams();

  const routeSymbol = decodeSymbolParam(encodedSymbol);
  const effectiveLayoutId = layoutId ?? DEFAULT_LAYOUT_ID;

  const searchParams = getDeepDiveSearchParams(location.search);
  const [selectedMarketAccounts, setSelectedMarketAccounts] = useState<string[]>(
    searchParams.marketAccountNames ?? EMPTY_ARRAY
  );
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>(searchParams.marketNames ?? EMPTY_ARRAY);
  const [selectedAggregation, setSelectedAggregation] = useState<string | undefined>(searchParams.aggregation);

  const deepDiveSecurities = useMemo(() => searchableSecurities.filter(s => !isMultileg(s)), [searchableSecurities]);
  const effectiveSymbol = routeSymbol ?? deepDiveSecurities[0]?.Symbol;
  const security = effectiveSymbol ? securitiesBySymbol.get(effectiveSymbol) : undefined;

  const [showMarkets, setShowMarkets] = useState(false);
  const [chartResolution, setChartResolution] = useState<ResolutionEnumNoPrefix>('1H');
  const [priceIncrement, setPriceIncrement] = useState<string | undefined>(security?.DefaultPriceIncrement);
  const [showCumQty, setShowCumQty] = useState(true);
  const [annotations, setAnnotations] = useState<AgAnnotation[] | undefined>();
  const [chartType, setChartType] = useState<AgPriceVolumeChartType | undefined>('candlestick');
  const [moduleIndex, setModuleIndex] = useState<MobileSymbolDeepDiveModuleIndex>(0);

  useEffect(() => {
    const urlParams = getDeepDiveSearchParams(location.search);
    setSelectedMarketAccounts(prev =>
      isEqual(prev, urlParams.marketAccountNames) ? prev : urlParams.marketAccountNames
    );
    setSelectedMarkets(prev => (isEqual(prev, urlParams.marketNames) ? prev : urlParams.marketNames));
    setSelectedAggregation(prev => (prev === urlParams.aggregation ? prev : urlParams.aggregation));
  }, [location.search]);

  useEffect(() => {
    if (effectiveSymbol == null) {
      return;
    }
    mixpanel.track(MixpanelEvent.ViewDeepDive, { [MixpanelEventProperty.ProductType]: security?.ProductType });
  }, [mixpanel, effectiveSymbol, security?.ProductType]);

  const syncRoute = useCallback(
    (params: { symbol: string; marketAccountNames?: string[]; marketNames?: string[]; aggregation?: string }) => {
      const nextPath = getDeepDiveRoute({
        symbol: params.symbol,
        layoutId: effectiveLayoutId,
        navGroupName,
        marketAccountNames: params.marketAccountNames,
        marketNames: params.marketNames,
        aggregation: params.aggregation,
      });
      if (`${location.pathname}${location.search}` !== nextPath) {
        navigate(nextPath, { replace: true });
      }
    },
    [effectiveLayoutId, navGroupName, location.pathname, location.search, navigate]
  );

  const setMarketSelection = useCallback(
    ({ marketAccounts, markets, aggregation }: DeepDiveMarketSelection) => {
      setSelectedMarketAccounts(marketAccounts);
      setSelectedMarkets(markets);
      setSelectedAggregation(aggregation);
      if (effectiveSymbol) {
        syncRoute({
          symbol: effectiveSymbol,
          marketAccountNames: marketAccounts,
          marketNames: markets,
          aggregation,
        });
      }
    },
    [effectiveSymbol, syncRoute]
  );

  const closeDeepDive = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const setSymbol = useCallback(
    (nextSymbol: string) => {
      const nextPanelParams = buildSymbolChangePanelParams(nextSymbol);
      setSelectedMarketAccounts(nextPanelParams.marketAccounts);
      setSelectedMarkets(nextPanelParams.markets);
      setSelectedAggregation(nextPanelParams.aggregation);
      setAnnotations(undefined);
      syncRoute({
        symbol: nextSymbol,
        marketAccountNames: nextPanelParams.marketAccounts,
        marketNames: nextPanelParams.markets,
        aggregation: nextPanelParams.aggregation,
      });
    },
    [buildSymbolChangePanelParams, syncRoute]
  );

  const setTrackedModuleIndex = useCallback(
    (nextIndexOrUpdater: SetStateAction<MobileSymbolDeepDiveModuleIndex>) => {
      setModuleIndex(prevIndex => {
        const nextIndex = typeof nextIndexOrUpdater === 'function' ? nextIndexOrUpdater(prevIndex) : nextIndexOrUpdater;

        if (nextIndex !== prevIndex) {
          mixpanel.track(MixpanelEvent.ChangeCardSlide, {
            [MixpanelEventProperty.FromIndex]: prevIndex,
            [MixpanelEventProperty.ToIndex]: nextIndex,
            [MixpanelEventProperty.TabIndex]: nextIndex,
            [MixpanelEventProperty.TabLabel]: MOBILE_SYMBOL_DEEP_DIVE_MODULES[nextIndex].label,
            [MixpanelEventProperty.ProductType]: security?.ProductType,
          });
        }

        return nextIndex;
      });
    },
    [mixpanel, security?.ProductType]
  );

  if (effectiveSymbol == null) {
    return null;
  }

  return (
    <MixpanelSourceContext.Provider value={MixpanelEventSource.DeepDive}>
      <DeepDiveProvider
        symbol={effectiveSymbol}
        setSymbol={setSymbol}
        selectedMarkets={selectedMarkets}
        selectedMarketAccounts={selectedMarketAccounts}
        selectedAggregation={selectedAggregation}
        setMarketSelection={setMarketSelection}
        setShowMarkets={setShowMarkets}
        showMarkets={showMarkets}
        closeDeepDive={closeDeepDive}
        chartResolution={chartResolution}
        setChartResolution={setChartResolution}
        setShowCumQty={setShowCumQty}
        setPriceIncrement={setPriceIncrement}
        showCumQty={showCumQty}
        priceIncrement={priceIncrement}
        annotations={annotations}
        chartType={chartType}
        setAnnotations={setAnnotations}
        setChartType={setChartType}
      >
        <MobileSymbolDeepDiveModuleProvider moduleIndex={moduleIndex} setModuleIndex={setTrackedModuleIndex}>
          <MobileSymbolDeepDiveView />
        </MobileSymbolDeepDiveModuleProvider>
      </DeepDiveProvider>
    </MixpanelSourceContext.Provider>
  );
}
